import { useState } from 'react';
import { Briefcase, Download, HelpCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import ToolPage from '@/components/ToolPage';

interface Question {
  id: number;
  text: string;
  options: {
    text: string;
    score: number;
  }[];
}

interface RiskProfile {
  type: string;
  description: string;
  recommendedAllocation: {
    stocks: number;
    bonds: number;
    cash: number;
  };
  characteristics: string[];
}

const questions: Question[] = [
  {
    id: 1,
    text: "What is your primary investment goal?",
    options: [
      { text: "Preserving my wealth and avoiding losses", score: 1 },
      { text: "Generating stable income", score: 2 },
      { text: "Growing my wealth over time", score: 3 },
      { text: "Maximizing long-term returns", score: 4 }
    ]
  },
  {
    id: 2,
    text: "How long do you plan to invest your money before you need it?",
    options: [
      { text: "Less than 3 years", score: 1 },
      { text: "3-5 years", score: 2 },
      { text: "5-10 years", score: 3 },
      { text: "More than 10 years", score: 4 }
    ]
  },
  {
    id: 3,
    text: "If your investment portfolio lost 20% of its value in a market downturn, what would you do?",
    options: [
      { text: "Sell everything to prevent further losses", score: 1 },
      { text: "Sell some investments to reduce risk", score: 2 },
      { text: "Hold steady and make no changes", score: 3 },
      { text: "Buy more investments at lower prices", score: 4 }
    ]
  },
  {
    id: 4,
    text: "How much financial knowledge and investment experience do you have?",
    options: [
      { text: "Very little knowledge or experience", score: 1 },
      { text: "Basic knowledge of investments", score: 2 },
      { text: "Good knowledge and some experience", score: 3 },
      { text: "Extensive knowledge and experience", score: 4 }
    ]
  },
  {
    id: 5,
    text: "What percentage of your monthly income can you save for investing?",
    options: [
      { text: "Less than 5%", score: 1 },
      { text: "5-10%", score: 2 },
      { text: "10-20%", score: 3 },
      { text: "More than 20%", score: 4 }
    ]
  },
  {
    id: 6,
    text: "How would you describe your approach to financial decisions?",
    options: [
      { text: "Very cautious and risk-averse", score: 1 },
      { text: "Somewhat cautious", score: 2 },
      { text: "Balanced between cautious and aggressive", score: 3 },
      { text: "Aggressive and willing to take risks", score: 4 }
    ]
  }
];

const riskProfiles: { [key: string]: RiskProfile } = {
  conservative: {
    type: "Conservative",
    description: "You prefer stability and are more concerned with protecting your wealth than growing it rapidly.",
    recommendedAllocation: {
      stocks: 20,
      bonds: 60,
      cash: 20
    },
    characteristics: [
      "Focus on wealth preservation",
      "Prefer stable, low-risk investments",
      "Comfortable with lower potential returns",
      "May be nearing retirement or have short-term goals"
    ]
  },
  moderatelyConservative: {
    type: "Moderately Conservative",
    description: "You seek modest growth while maintaining a good degree of stability in your investments.",
    recommendedAllocation: {
      stocks: 40,
      bonds: 50,
      cash: 10
    },
    characteristics: [
      "Balance between growth and stability",
      "Willing to accept some market fluctuations",
      "Moderate risk tolerance",
      "Medium-term investment horizon"
    ]
  },
  balanced: {
    type: "Balanced",
    description: "You aim for a balance between growth and stability, accepting moderate market fluctuations.",
    recommendedAllocation: {
      stocks: 60,
      bonds: 35,
      cash: 5
    },
    characteristics: [
      "Equal focus on growth and stability",
      "Comfortable with market fluctuations",
      "Moderate to high risk tolerance",
      "Longer-term investment horizon"
    ]
  },
  aggressive: {
    type: "Aggressive",
    description: "You prioritize growth and are willing to accept significant market fluctuations for potentially higher returns.",
    recommendedAllocation: {
      stocks: 80,
      bonds: 15,
      cash: 5
    },
    characteristics: [
      "Focus on maximum growth",
      "Comfortable with significant market volatility",
      "High risk tolerance",
      "Long-term investment horizon"
    ]
  }
};

export default function RiskToleranceAssessment() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [riskProfile, setRiskProfile] = useState<RiskProfile | null>(null);
  const [showInfo, setShowInfo] = useState(false);

  const handleAnswer = (score: number) => {
    const newAnswers = [...answers, score];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      calculateRiskProfile(newAnswers);
      setShowResults(true);
    }
  };

  const calculateRiskProfile = (scores: number[]) => {
    const totalScore = scores.reduce((sum, score) => sum + score, 0);
    const averageScore = totalScore / scores.length;

    let profile: RiskProfile;
    if (averageScore <= 1.5) {
      profile = riskProfiles.conservative;
    } else if (averageScore <= 2.5) {
      profile = riskProfiles.moderatelyConservative;
    } else if (averageScore <= 3.5) {
      profile = riskProfiles.balanced;
    } else {
      profile = riskProfiles.aggressive;
    }

    setRiskProfile(profile);
  };

  const resetAssessment = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResults(false);
    setRiskProfile(null);
  };

  const downloadResults = () => {
    if (!riskProfile) return;

    let csv = 'Risk Tolerance Assessment Results\n\n';
    csv += `Risk Profile: ${riskProfile.type}\n`;
    csv += `Description: ${riskProfile.description}\n\n`;
    csv += 'Recommended Asset Allocation\n';
    csv += `Stocks: ${riskProfile.recommendedAllocation.stocks}%\n`;
    csv += `Bonds: ${riskProfile.recommendedAllocation.bonds}%\n`;
    csv += `Cash: ${riskProfile.recommendedAllocation.cash}%\n\n`;
    csv += 'Profile Characteristics\n';
    riskProfile.characteristics.forEach(char => {
      csv += `- ${char}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'risk-tolerance-assessment.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <ToolPage title="Risk Tolerance Assessment" description="Evaluate your investment risk tolerance and get personalized recommendations" icon={Briefcase}>
        {!showResults ? (
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="pt-8">
            <div className="flex justify-between items-center mb-8">
              <div className="flex-1">
                <span className="text-sm text-muted-foreground">Question {currentQuestion + 1} of {questions.length}</span>
                <div className="w-full bg-muted h-2 rounded-full mt-2">
                  <div className="bg-primary h-2 rounded-full transition-all duration-300" style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }} />
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowInfo(!showInfo)}>
                <HelpCircle className="w-5 h-5" />
              </Button>
            </div>

            {showInfo && (
              <div className="mb-6 p-4 bg-muted rounded-lg">
                <h3 className="font-medium mb-2">About This Assessment</h3>
                <p className="text-sm text-muted-foreground">This assessment helps determine your risk tolerance level and provides investment recommendations based on your answers. Be honest and consider your long-term financial goals when answering.</p>
              </div>
            )}

            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">{questions[currentQuestion].text}</h2>
              <div className="space-y-3">
                {questions[currentQuestion].options.map((option, index) => (
                  <Button key={index} variant="outline" className="w-full justify-start h-auto py-4 text-left" onClick={() => handleAnswer(option.score)}>
                    {option.text}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row justify-between items-center">
                <h2 className="text-xl font-semibold">Your Risk Profile</h2>
                <Button variant="secondary" size="sm" onClick={downloadResults}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Results
                </Button>
              </CardHeader>
              <CardContent>
              {riskProfile && (
                <div>
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold mb-2">{riskProfile.type}</h3>
                    <p className="text-muted-foreground">{riskProfile.description}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-medium mb-4">Recommended Asset Allocation</h4>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-muted-foreground">Stocks</span>
                            <span className="font-medium">{riskProfile.recommendedAllocation.stocks}%</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div className="bg-primary h-2 rounded-full" style={{ width: `${riskProfile.recommendedAllocation.stocks}%` }} />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-muted-foreground">Bonds</span>
                            <span className="font-medium">{riskProfile.recommendedAllocation.bonds}%</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div className="bg-brand-600 h-2 rounded-full" style={{ width: `${riskProfile.recommendedAllocation.bonds}%` }} />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-muted-foreground">Cash</span>
                            <span className="font-medium">{riskProfile.recommendedAllocation.cash}%</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div className="bg-muted-foreground h-2 rounded-full" style={{ width: `${riskProfile.recommendedAllocation.cash}%` }} />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium mb-4">Profile Characteristics</h4>
                      <ul className="space-y-2">
                        {riskProfile.characteristics.map((characteristic, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                            <span className="text-muted-foreground">{characteristic}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-8 pt-6 border-t">
                <Button className="w-full" onClick={resetAssessment}>Take Assessment Again</Button>
              </div>
              </CardContent>
            </Card>

            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-semibold mb-2">Important Notes</h3>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li>This assessment is for educational purposes only and should not be considered financial advice</li>
                    <li>Consider consulting with a financial advisor before making investment decisions</li>
                    <li>Your risk tolerance may change over time as your circumstances change</li>
                    <li>Regular portfolio rebalancing is important to maintain your target asset allocation</li>
                  </ul>
                </div>
              </div>
              </CardContent>
            </Card>
          </div>
        )}
    </ToolPage>
  );
}